import { Collapse, Form } from 'antd';
import LeftMenuDesign from '../../common/LeftMenuDesign/LeftMenuDesign';
import './LeftCabinetCustomDesignMenu.scss';
import InputComponent from '../../common/InputComponent/InputComponent';
import { Icon } from '../../icon/icon';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CabinetEditorContext } from '../CabinetEditor/CabinetEditor';
import ButtonSecondarySmall from '../../common/ButtonSecondary/ButtonSecondarySmall';
import { Divider } from 'antd';
import {
  evaluateExpression,
  getInputType,
} from '../../../utils/string/inputExpression';
import { BACK_THICKNESS_OPTIONS, InsideType } from '../../../constants/enum';
import { openNotification } from '../../common/Notification/openNotification';
import { roundTo } from '../../../utils/geometry/epsilon';
import { t } from 'i18next';
import SelectComponent from '../../common/SelectComponent/SelectComponent';

const LeftCabinetCustomDesignMenu = ({ open, form }) => {
  const {
    sceneRef,
    clonedModule,
    insideLoaderRef,
    mdCustomData,
    setMdCustomData,
    setListApplyToAll,
    listApplyToAll,
  } = useContext(CabinetEditorContext);
  const [formLayout, setFormLayout] = useState([]);

  const scaleTimerRef = useRef(null);

  const validatorSize = ({ value, min, max }) => {
    const type = getInputType(value);
    if (type === 'text') {
      return Promise.reject(new Error(t('Invalid size')));
    }
    if (['number', 'invalid'].includes(type)) {
      const num = (parseFloat(value) || 0) / 1000;
      if (min && max) {
        if (num > max || num < min) {
          return Promise.reject(
            new Error(`${min * 1000}mm ${t('to')} ${max * 1000}mm`)
          );
        }
      } else {
        if (num <= 0) {
          return Promise.reject(t('Invalid size'));
        }
      }
    }
    if (type === 'expression') {
      const num = evaluateExpression(value) / 1000;
      if (min && max) {
        if (num > max || num < min) {
          return Promise.reject(
            new Error(`${min * 1000}mm ${t('to')} ${max * 1000}mm`)
          );
        }
      } else {
        if (num <= 0) {
          return Promise.reject(t('Invalid size'));
        }
      }
    }
    return Promise.resolve();
  };

  const formForCurrentCabinet = {
    frame: [
      {
        key: 'frame',
        label: <span className="text-title-medium">{t('Frame')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            {mdCustomData.type?.includes('corner') ? (
              <>
                <div className="flex flex-col gap-6 self-stretch items-start">
                  <Form.Item
                    label={
                      <div className="flex items-center justify-between self-stretch">
                        <span className="label-text-name flex gap-4 items-center">
                          {t('Length')}
                          <span className="support-text-medium">
                            ({t('From')}{' '}
                            {(mdCustomData?.mdOptions?.limitRongK1?.min ?? 0) *
                              1000}{' '}
                            {t('to')}{' '}
                            {(mdCustomData?.mdOptions?.limitRongK1?.max ?? 0) *
                              1000}{' '}
                            mm)
                          </span>
                        </span>
                      </div>
                    }
                    labelCol={{ className: 'label-col-full' }}
                    tooltip={{
                      title: t('Cannot apply to all'),
                      className: 'w-16 h-16 flex absolute right-0',
                      getTooltipContainer: () => document.body,
                      icon: (
                        <span className="w-16 h-16 flex items-center">
                          <Icon.ICDeselect />
                        </span>
                      ),
                    }}
                    className="self-stretch"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (mdCustomData?.mdOptions?.limitRongK1) {
                            const { max, min } =
                              mdCustomData.mdOptions.limitRongK1;
                            return validatorSize({ value, min, max });
                          }
                        },
                      },
                    ]}
                    name={'rongK1'}
                  >
                    <InputComponent
                      addonBefore={
                        <span className="text-body-medium">
                          {t('Left block')}
                        </span>
                      }
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={t('Enter')}
                    />
                  </Form.Item>
                  <Form.Item
                    label={
                      <span className="support-text-medium">
                        ({t('From')}{' '}
                        {(mdCustomData?.mdOptions?.limitRongK2?.min ?? 0) *
                          1000}{' '}
                        {t('to')}{' '}
                        {(mdCustomData?.mdOptions?.limitRongK2?.max ?? 0) *
                          1000}{' '}
                        mm)
                      </span>
                    }
                    className="self-stretch"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (mdCustomData?.mdOptions?.limitRongK2) {
                            const { max, min } =
                              mdCustomData.mdOptions.limitRongK2;
                            return validatorSize({
                              value,
                              min,
                              max,
                            });
                          }
                        },
                      },
                    ]}
                    name={'rongK2'}
                  >
                    <InputComponent
                      addonBefore={
                        <span className="text-body-medium">
                          {t('Right block')}
                        </span>
                      }
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={t('Enter')}
                    />
                  </Form.Item>
                </div>
              </>
            ) : (
              <>
                <Form.Item
                  label={
                    <div className="flex items-center justify-between self-stretch">
                      <span className="label-text-name flex gap-4 items-center">
                        {t('Length')}
                        <span className="support-text-medium">
                          ({t('From')}{' '}
                          {(mdCustomData?.mdLimitedDimensions?.width?.min ??
                            0) * 1000}{' '}
                          {t('to')}{' '}
                          {(mdCustomData?.mdLimitedDimensions?.width?.max ??
                            0) * 1000}{' '}
                          mm)
                        </span>
                      </span>
                    </div>
                  }
                  labelCol={{ className: 'label-col-full' }}
                  tooltip={{
                    title: t('Cannot apply all'),
                    className: 'w-16 h-16 flex absolute right-0',
                    getTooltipContainer: () => document.body,
                    icon: (
                      <span className="w-16 h-16 flex items-center">
                        <Icon.ICDeselect />
                      </span>
                    ),
                  }}
                  name={'width'}
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (mdCustomData?.mdLimitedDimensions?.width) {
                          const { max, min } =
                            mdCustomData.mdLimitedDimensions.width;
                          return validatorSize({
                            value,
                            min,
                            max,
                          });
                        }
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonAfter={<span className="text-body-medium">mm</span>}
                    placeholder={t('Enter')}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Depth')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdCustomData?.mdLimitedDimensions?.depth?.min ?? 0) *
                      1000}{' '}
                    {t('to')}{' '}
                    {(mdCustomData?.mdLimitedDimensions?.depth?.max ?? 0) *
                      1000}{' '}
                    mm)
                  </span>
                </span>
              }
              name={'depth'}
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    if (mdCustomData?.mdLimitedDimensions?.depth) {
                      const { max, min } =
                        mdCustomData.mdLimitedDimensions.depth;
                      return validatorSize({
                        value,
                        min,
                        max,
                      });
                    }
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Height')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdCustomData?.mdLimitedDimensions?.height?.min ?? 0) *
                      1000}{' '}
                    {t('to')}{' '}
                    {(mdCustomData?.mdLimitedDimensions?.height?.max ?? 0) *
                      1000}{' '}
                    mm)
                  </span>
                </span>
              }
              className="self-stretch"
              name={'height'}
              rules={[
                {
                  validator: (_, value) => {
                    if (mdCustomData?.mdLimitedDimensions?.height) {
                      const { max, min } =
                        mdCustomData.mdLimitedDimensions.height;
                      return validatorSize({
                        value,
                        min,
                        max,
                      });
                    }
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
            <Form.Item
              className="flex items-center gap-8 self-stretch"
              style={{ marginTop: '-10px' }}
            >
              <div className="flex items-center gap-8 self-stretch">
                <Divider
                  type="vertical"
                  style={{ margin: 0, height: '24px' }}
                />
                <ButtonSecondarySmall
                  posIcon={<Icon.ICSelectAll />}
                  title={t('Apply to all')}
                  type={'button'}
                  className={
                    ['height', 'depth'].some(item =>
                      listApplyToAll.includes(item)
                    )
                      ? 'active'
                      : ''
                  }
                  onClick={() => {
                    setListApplyToAll(prev => {
                      const names = ['height', 'depth'];
                      if (names.some(item => prev.includes(item))) {
                        return [
                          ...prev.filter(item =>
                            names.some(name => name !== item)
                          ),
                        ];
                      } else return [...prev, ...names];
                    });
                  }}
                />
              </div>
            </Form.Item>

            {/* {mdCustomData.mdOptions.optionCua && (
              <Form.Item label={"Loại cửa"}>
                <InputComponent
                 
                  placeholder={"Nhập"}
                  defaultValue={mdCustomData.mdOptions.optionCua}
                  onChange={(e) => {
                    const optionCua = parseFloat(e.target.value) || 0;
                    handleScaleModule({
                      optionCua: optionCua,
                    });
                  }}
                />
              </Form.Item>
            )} */}
          </div>
        ),
      },
    ],
    backPlank: [
      {
        key: 'backPlank',
        label: <span className="text-title-medium">{t('Back plank')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item
              layout="vertical"
              name={'DDTHau'}
              label={t('Back thickness')}
              className="self-stretch"
            >
              <SelectComponent
                options={BACK_THICKNESS_OPTIONS}
                placeholder={t('Select back plank type')}
                style={{ flex: '1 0 0' }}
                size={'default'}
              />
            </Form.Item>

            <Form.Item
              name={'cabinetLegsHeightApplyToAll'}
              style={{ marginTop: '-10px' }}
            >
              <div className="flex items-center gap-8 self-stretch">
                <Divider
                  type="vertical"
                  style={{ margin: 0, height: '24px' }}
                />
                <ButtonSecondarySmall
                  posIcon={<Icon.ICSelectAll />}
                  title={t('Apply to all')}
                  className={listApplyToAll.includes('DDTHau') ? 'active' : ''}
                  type={'button'}
                  onClick={() => {
                    setListApplyToAll(prev => {
                      const name = 'DDTHau';
                      if (prev.includes(name)) {
                        return [...prev.filter(item => item !== name)];
                      } else return [...prev, name];
                    });
                  }}
                />
              </div>
            </Form.Item>
          </div>
        ),
      },
    ],
    legs: [
      {
        key: 'legs',
        label: <span className="text-title-medium">{t('Cabinet legs')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item
              name={'caoChan'}
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Height')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdCustomData?.mdOptions?.limitCaoChan?.min ?? 0) * 1000}{' '}
                    {t('to')}{' '}
                    {(mdCustomData?.mdOptions?.limitCaoChan?.max ?? 0) * 1000}{' '}
                    mm)
                  </span>
                </span>
              }
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    if (mdCustomData?.mdOptions?.limitCaoChan) {
                      const { max, min } = mdCustomData.mdOptions.limitCaoChan;
                      return validatorSize({
                        value,
                        min,
                        max,
                      });
                    }
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>

            <Form.Item
              name={'cabinetLegsHeightApplyToAll'}
              style={{ marginTop: '-10px' }}
            >
              <div className="flex items-center gap-8 self-stretch">
                <Divider
                  type="vertical"
                  style={{ margin: 0, height: '24px' }}
                />
                <ButtonSecondarySmall
                  posIcon={<Icon.ICSelectAll />}
                  title={t('Apply to all')}
                  className={listApplyToAll.includes('caoChan') ? 'active' : ''}
                  type={'button'}
                  onClick={() => {
                    setListApplyToAll(prev => {
                      const name = 'caoChan';
                      if (prev.includes(name)) {
                        return [...prev.filter(item => item !== name)];
                      } else return [...prev, name];
                    });
                  }}
                />
              </div>
            </Form.Item>
          </div>
        ),
      },
    ],
    // refrigerator: [
    //   {
    //     key: "frame",
    //     label: <span className="text-title-medium">Khung tủ</span>,
    //     children: (
    //       <div className="flex flex-col gap-16 items-start self-stretch">
    //         {mdCustomData.mdDimensions.rongK1 !== undefined &&
    //         mdCustomData.mdDimensions.rongK2 !== undefined ? (
    //           <React.Fragment>
    //             <Form.Item label={"Chiều rộng K1"} name={"rongK1"}>
    //               <InputComponent
    //                 addonAfter={<span className="text-body-medium">mm</span>}
    //                 placeholder={"Nhập"}
    //                 defaultValue={mdCustomData.mdDimensions.rongK1 * 1000}
    //                 onChange={(e) => {
    //                   if (getInputType(e.target.value) === "number") {
    //                     const rongK1 = parseFloat(e.target.value) || 0;
    //                     handleScaleModule({
    //                       rongK1: rongK1 / 1000, // Convert mm to m
    //                     });
    //                   }
    //                 }}
    //               />
    //             </Form.Item>
    //             <Form.Item label={"Chiều rộng K2"} name={"rongK2"}>
    //               <InputComponent
    //                 addonAfter={<span className="text-body-medium">mm</span>}
    //                 placeholder={"Nhập"}
    //                 defaultValue={mdCustomData.mdDimensions.rongK2 * 1000}
    //                 onChange={(e) => {
    //                   if (getInputType(e.target.value) === "number") {
    //                     const rongK2 = parseFloat(e.target.value) || 0;
    //                     handleScaleModule({
    //                       rongK2: rongK2 / 1000, // Convert mm to m
    //                     });
    //                   }
    //                 }}
    //               />
    //             </Form.Item>
    //           </React.Fragment>
    //         ) : (
    //           <Form.Item
    //             label={
    //               <div className="flex items-center justify-between self-stretch">
    //                 <span className="label-text-name flex gap-4 items-center">
    //                   Chiều dài
    //                   <span className="support-text-medium">
    //                     (Từ{" "}
    //                     {(mdCustomData?.mdLimitedDimensions?.width?.min ?? 0) *
    //                       1000}{" "}
    //                     -{" "}
    //                     {(mdCustomData?.mdLimitedDimensions?.depth?.width ??
    //                       0) * 1000}{" "}
    //                     mm)
    //                   </span>
    //                 </span>
    //                 <Tooltip title={"Không thể áp dụng tất cả"}>
    //                   <span className="w-16 h-16 flex">
    //                     <Icon.ICDeselect />
    //                   </span>
    //                 </Tooltip>
    //               </div>
    //             }
    //             name={"width"}>
    //             <InputComponent
    //               addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"Nhập"}
    //               defaultValue={mdCustomData.mdDimensions.width * 1000}
    //               onChange={(e) => {
    //                 if (getInputType(e.target.value) === "number") {
    //                   const width = parseFloat(e.target.value) || 0;
    //                   handleScaleModule({
    //                     width: width / 1000, // Convert mm to m
    //                   });
    //                 }
    //               }}
    //             />
    //           </Form.Item>
    //         )}

    //         <Form.Item
    //           label={
    //             <span className="label-text-name flex gap-4 items-center">
    //               Chiều sâu
    //               <span className="support-text-medium">
    //                 (Từ{" "}
    //                 {(mdCustomData?.mdLimitedDimensions?.depth?.min ?? 0) *
    //                   1000}{" "}
    //                 -{" "}
    //                 {(mdCustomData?.mdLimitedDimensions?.depth?.max ?? 0) *
    //                   1000}{" "}
    //                 mm)
    //               </span>
    //             </span>
    //           }
    //           name={"depth"}
    //           validateTrigger="onChange">
    //           <InputComponent
    //             addonAfter={<span className="text-body-medium">mm</span>}
    //             placeholder={"Nhập"}
    //             defaultValue={mdCustomData.mdDimensions.height * 1000}
    //             onChange={(e) => {
    //               if (getInputType(e.target.value) === "number") {
    //                 const height = parseFloat(e.target.value) || 0;
    //                 handleScaleModule({
    //                   height: height / 1000, // Convert mm to m
    //                 });
    //               }
    //             }}
    //           />
    //         </Form.Item>
    //         <Form.Item
    //           label={
    //             <span className="label-text-name flex gap-4 items-center">
    //               Chiều cao
    //               <span className="support-text-medium">
    //                 (Từ{" "}
    //                 {(mdCustomData?.mdLimitedDimensions?.height?.min ?? 0) *
    //                   1000}{" "}
    //                 -{" "}
    //                 {(mdCustomData?.mdLimitedDimensions?.height?.max ?? 0) *
    //                   1000}{" "}
    //                 mm)
    //               </span>
    //             </span>
    //           }
    //           name={"height"}
    //           validateTrigger="onChange">
    //           <InputComponent
    //             addonAfter={<span className="text-body-medium">mm</span>}
    //             placeholder={"Nhập"}
    //             defaultValue={mdCustomData.mdDimensions.height * 1000}
    //             onChange={(e) => {
    //               if (getInputType(e.target.value) === "number") {
    //                 const height = parseFloat(e.target.value) || 0;
    //                 handleScaleModule({
    //                   height: height / 1000, // Convert mm to m
    //                 });
    //               }
    //             }}
    //           />
    //         </Form.Item>
    //         {/* <Form.Item label={"Dày ván"}>
    //           <InputComponent

    //             addonAfter={<span className="text-body-medium">mm</span>}
    //             placeholder={"Nhập"}
    //             defaultValue={
    //               mdCustomData.mdDimensions.thickness * 1000
    //             }
    //             onChange={(e) => {
    //               const thickness = parseFloat(e.target.value) || 0;
    //               handleScaleModule({
    //                 thickness: thickness / 1000, // Convert mm to m
    //               });
    //             }}
    //           />
    //         </Form.Item> */}

    //         {mdCustomData.mdOptions.optionCua && (
    //           <Form.Item label={"Loại cửa"}>
    //             <InputComponent
    //               // addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"Nhập"}
    //               defaultValue={mdCustomData.mdOptions.optionCua}
    //               onChange={(e) => {
    //                 if (getInputType(e.target.value) === "number") {
    //                   const optionCua = parseFloat(e.target.value) || 0;
    //                   handleScaleModule({
    //                     optionCua: optionCua,
    //                   });
    //                 }
    //               }}
    //             />
    //           </Form.Item>
    //         )}
    //       </div>
    //     ),
    //   },
    //   {
    //     key: "cabinetLegs",
    //     label: <span className="text-title-medium">Chân tủ</span>,
    //     children: (
    //       <div className="flex flex-col gap-16 items-start self-stretch">
    //         <Form.Item label={"Cao chân"} className="self-stretch">
    //           <div className="flex items-center gap-4">
    //             <InputComponent
    //               addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"Nhập"}
    //             />
    //             <ButtonIconGhostSmall icon={<Icon.ICLockOpen />} />
    //           </div>
    //         </Form.Item>
    //         <Form.Item label={"Lùi chân"} className="self-stretch">
    //           <div className="flex gap-8 items-center grow">
    //             <Slider style={{ flex: "1 0 0" }} />
    //             <InputComponent
    //               addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"--"}
    //               size={"small"}
    //               style={{ width: "84px" }}
    //             />
    //             <ButtonIconGhostSmall icon={<Icon.ICRestart />} />
    //           </div>
    //         </Form.Item>
    //       </div>
    //     ),
    //   },
    //   {
    //     key: "cabinetTrim",
    //     label: <span className="text-title-medium">Nẹp tủ</span>,
    //     children: (
    //       <div className="flex flex-col gap-16 items-start self-stretch">
    //         <Form.Item label={"Nẹp hai bên"} className="self-stretch">
    //           <div className="flex items-center gap-4">
    //             <InputComponent
    //               addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"Nhập"}
    //             />
    //             <ButtonIconGhostSmall icon={<Icon.ICLockOpen />} />
    //           </div>
    //         </Form.Item>
    //       </div>
    //     ),
    //   },
    // ],
    // dryCabinet: [
    //   {
    //     key: "frame",
    //     label: <span className="text-title-medium">Khung tủ</span>,
    //     children: (
    //       <div className="flex flex-col gap-16 items-start self-stretch">
    //         {mdCustomData.mdDimensions.rongK1 !== undefined &&
    //         mdCustomData.mdDimensions.rongK2 !== undefined ? (
    //           <React.Fragment>
    //             <Form.Item label={"Chiều rộng K1"} name={"rongK1"}>
    //               <InputComponent
    //                 addonAfter={<span className="text-body-medium">mm</span>}
    //                 placeholder={"Nhập"}
    //                 defaultValue={mdCustomData.mdDimensions.rongK1 * 1000}
    //                 onChange={(e) => {
    //                   if (getInputType(e.target.value) === "number") {
    //                     const rongK1 = parseFloat(e.target.value) || 0;
    //                     handleScaleModule({
    //                       rongK1: rongK1 / 1000, // Convert mm to m
    //                     });
    //                   }
    //                 }}
    //               />
    //             </Form.Item>
    //             <Form.Item label={"Chiều rộng K2"} name={"rongK2"}>
    //               <InputComponent
    //                 addonAfter={<span className="text-body-medium">mm</span>}
    //                 placeholder={"Nhập"}
    //                 defaultValue={mdCustomData.mdDimensions.rongK2 * 1000}
    //                 onChange={(e) => {
    //                   if (getInputType(e.target.value) === "number") {
    //                     const rongK2 = parseFloat(e.target.value) || 0;
    //                     handleScaleModule({
    //                       rongK2: rongK2 / 1000, // Convert mm to m
    //                     });
    //                   }
    //                 }}
    //               />
    //             </Form.Item>
    //           </React.Fragment>
    //         ) : (
    //           <Form.Item
    //             label={
    //               <div className="flex items-center justify-between self-stretch">
    //                 <span className="label-text-name flex gap-4 items-center">
    //                   Chiều dài
    //                   <span className="support-text-medium">
    //                     (Từ{" "}
    //                     {(mdCustomData?.mdLimitedDimensions?.width?.min ?? 0) *
    //                       1000}{" "}
    //                     -{" "}
    //                     {(mdCustomData?.mdLimitedDimensions?.depth?.width ??
    //                       0) * 1000}{" "}
    //                     mm)
    //                   </span>
    //                   <Tooltip title={"Không thể áp dụng tất cả"}>
    //                     <span className="w-16 h-16 flex">
    //                       <Icon.ICDeselect />
    //                     </span>
    //                   </Tooltip>
    //                 </span>
    //               </div>
    //             }
    //             name={"width"}>
    //             <InputComponent
    //               addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"Nhập"}
    //               defaultValue={mdCustomData.mdDimensions.width * 1000}
    //               onChange={(e) => {
    //                 if (getInputType(e.target.value) === "number") {
    //                   const width = parseFloat(e.target.value) || 0;
    //                   handleScaleModule({
    //                     width: width / 1000, // Convert mm to m
    //                   });
    //                 }
    //               }}
    //             />
    //           </Form.Item>
    //         )}

    //         <Form.Item
    //           label={
    //             <span className="label-text-name flex gap-4 items-center">
    //               Chiều cao
    //               <span className="support-text-medium">
    //                 (Từ{" "}
    //                 {(mdCustomData?.mdLimitedDimensions?.height?.min ?? 0) *
    //                   1000}{" "}
    //                 -{" "}
    //                 {(mdCustomData?.mdLimitedDimensions?.height?.max ?? 0) *
    //                   1000}{" "}
    //                 mm)
    //               </span>
    //             </span>
    //           }
    //           name={"height"}
    //           validateTrigger="onChange">
    //           <InputComponent
    //             addonAfter={<span className="text-body-medium">mm</span>}
    //             placeholder={"Nhập"}
    //             defaultValue={mdCustomData.mdDimensions.height * 1000}
    //             onChange={(e) => {
    //               if (getInputType(e.target.value) === "number") {
    //                 const height = parseFloat(e.target.value) || 0;
    //                 handleScaleModule({
    //                   height: height / 1000, // Convert mm to m
    //                 });
    //               }
    //             }}
    //           />
    //         </Form.Item>

    //         <Form.Item
    //           label={
    //             <span className="label-text-name flex gap-4 items-center">
    //               Chiều sâu
    //               <span className="support-text-medium">
    //                 (Từ{" "}
    //                 {(mdCustomData?.mdLimitedDimensions?.depth?.min ?? 0) *
    //                   1000}{" "}
    //                 -{" "}
    //                 {(mdCustomData?.mdLimitedDimensions?.depth?.max ?? 0) *
    //                   1000}{" "}
    //                 mm)
    //               </span>
    //             </span>
    //           }
    //           name={"depth"}
    //           validateTrigger="onChange">
    //           <InputComponent
    //             addonAfter={<span className="text-body-medium">mm</span>}
    //             placeholder={"Nhập"}
    //             defaultValue={mdCustomData.mdDimensions.depth * 1000}
    //             onChange={(e) => {
    //               if (getInputType(e.target.value) === "number") {
    //                 const depth = parseFloat(e.target.value) || 0;
    //                 handleScaleModule({
    //                   depth: depth / 1000, // Convert mm to m
    //                 });
    //               }
    //             }}
    //           />
    //         </Form.Item>
    //         {/* <Form.Item label={"Dày ván"}>
    //           <InputComponent

    //             addonAfter={<span className="text-body-medium">mm</span>}
    //             placeholder={"Nhập"}
    //             defaultValue={
    //               mdCustomData.mdDimensions.thickness * 1000
    //             }
    //             onChange={(e) => {
    //               const thickness = parseFloat(e.target.value) || 0;
    //               handleScaleModule({
    //                 thickness: thickness / 1000, // Convert mm to m
    //               });
    //             }}
    //           />
    //         </Form.Item> */}

    //         {mdCustomData.mdOptions.optionCua && (
    //           <Form.Item label={"Loại cửa"}>
    //             <InputComponent
    //               // addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"Nhập"}
    //               defaultValue={mdCustomData.mdOptions.optionCua}
    //               onChange={(e) => {
    //                 if (getInputType(e.target.value) === "number") {
    //                   const optionCua = parseFloat(e.target.value) || 0;
    //                   handleScaleModule({
    //                     optionCua: optionCua,
    //                   });
    //                 }
    //               }}
    //             />
    //           </Form.Item>
    //         )}
    //       </div>
    //     ),
    //   },
    //   {
    //     key: "cabinetLegs",
    //     label: <span className="text-title-medium">Chân tủ</span>,
    //     children: (
    //       <div className="flex flex-col gap-16 items-start self-stretch">
    //         <Form.Item label={"Cao chân"} className="self-stretch">
    //           <div className="flex items-center gap-4">
    //             <InputComponent
    //               addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"Nhập"}
    //             />
    //             <ButtonIconGhostSmall icon={<Icon.ICLockOpen />} />
    //           </div>
    //         </Form.Item>
    //         <Form.Item label={"Lùi chân"} className="self-stretch">
    //           <div className="flex gap-8 items-center grow">
    //             <Slider style={{ flex: "1 0 0" }} />
    //             <InputComponent
    //               addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"--"}
    //               size={"small"}
    //               style={{ width: "84px" }}
    //             />
    //             <ButtonIconGhostSmall icon={<Icon.ICRestart />} />
    //           </div>
    //         </Form.Item>
    //       </div>
    //     ),
    //   },
    //   {
    //     key: "cabinetTrim",
    //     label: <span className="text-title-medium">Nẹp tủ</span>,
    //     children: (
    //       <div className="flex flex-col gap-16 items-start self-stretch">
    //         <Form.Item label={"Nẹp hai bên"} className="self-stretch">
    //           <div className="flex items-center gap-4">
    //             <InputComponent
    //               addonAfter={<span className="text-body-medium">mm</span>}
    //               placeholder={"Nhập"}
    //             />
    //             <ButtonIconGhostSmall icon={<Icon.ICLockOpen />} />
    //           </div>
    //         </Form.Item>
    //       </div>
    //     ),
    //   },
    // ],
  };

  // Debounced scale handler
  const debouncedValidate = useCallback(
    (changedValues, delay = 300) => {
      if (scaleTimerRef.current) {
        clearTimeout(scaleTimerRef.current);
      }

      scaleTimerRef.current = setTimeout(async () => {
        try {
          const name = Object.keys(changedValues)[0];
          const value = changedValues[name];
          if (getInputType(changedValues[name]) === 'number') {
            if (open) await form.validateFields([name]); // chỉ validate field này
            if (['width', 'height', 'depth'].includes(name)) {
              handleScaleModule({ [name]: value / 1000 });
            } else if (
              ['caoChan', 'rongK1', 'rongK2', 'DDTHau'].includes(name)
            ) {
              handleScaleModule({}, { [name]: value / 1000 });
            }
          }
        } catch {
          // Handle validation errors if needed
        }
      }, delay);
    },
    [sceneRef.current, clonedModule, insideLoaderRef?.current, open]
  );

  const handleScaleModule = (mdDimensions, mdOptions) => {
    try {
      if (
        mdDimensions?.width === 0 ||
        mdDimensions?.height === 0 ||
        mdDimensions?.depth === 0
      ) {
        return;
      }

      if (mdDimensions?.width) {
        let check = false;
        for (let i = 0; i < clonedModule.listInside.length; i++) {
          const inside = clonedModule.listInside[i];
          if (
            inside.gltf.userData.type === InsideType.ACCESSORY_DISH_LOWER ||
            inside.gltf.userData.type === InsideType.ACCESSORY_DISH_UPPER
          ) {
            check = true;
            break;
          }
        }
        if (check) {
          openNotification({
            key: 'cant-add-door-existing-door',
            icon: <Icon.ICWarning />,
            iconColor: '#F97D16',
            title: t("Can't change size"),
            description: t(
              'This cabinet frame has accessories with fixed dimensions, so you cannot change the length.'
            ),
            placement: 'bottomRight',
            closable: true,
            duration: 4,
          });
          return;
        }
      }

      setMdCustomData(prevData => {
        const newData = {
          ...prevData,
          mdDimensions: {
            ...prevData.mdDimensions,
            ...mdDimensions,
          },
          mdOptions: {
            ...prevData.mdOptions,
            ...mdOptions,
          },
        };

        const oldDimensions = { ...prevData.mdDimensions };
        const oldOptions = { ...prevData.mdOptions };

        // Scale module với newData
        clonedModule.scaleModuleWithFormular(
          newData.mdDimensions,
          newData.mdOptions
        );

        //Vẽ lại box
        setTimeout(() => {
          sceneRef.current.handleDrawBorder();
        }, 0);

        // Scale inside components
        if (insideLoaderRef?.current) {
          insideLoaderRef?.current.scaleInsideAfterModuleScale(
            clonedModule,
            oldDimensions,
            newData.mdDimensions,
            oldOptions,
            newData.mdOptions
          );
        }

        return newData;
      });
    } catch (error) {
      console.error('Error scaling module:', error);
    }
  };

  useEffect(() => {
    if (open)
      form.setFieldsValue({
        width: roundTo(mdCustomData.mdDimensions.width * 1000),
        height: roundTo(mdCustomData.mdDimensions.height * 1000),
        depth: roundTo(mdCustomData.mdDimensions.depth * 1000),
        DDTHau: roundTo(mdCustomData.mdOptions.DDTHau * 1000),
        caoChan: roundTo(mdCustomData.mdOptions.caoChan * 1000),
        rongK1: roundTo(mdCustomData.mdOptions.rongK1 * 1000),
        rongK2: roundTo(mdCustomData.mdOptions.rongK2 * 1000),
      });
  }, [mdCustomData, open]);

  useEffect(() => {
    // let formLayout = formForCurrentCabinet[('frame')];
    let formLayout = [
      ...formForCurrentCabinet.frame,
      ...formForCurrentCabinet.backPlank,
    ];
    if (mdCustomData.type?.includes('lower')) {
      formLayout = [...formLayout, ...formForCurrentCabinet.legs];
    }
    setFormLayout([...formLayout]);
  }, [mdCustomData.type, listApplyToAll]);

  return (
    <LeftMenuDesign
      open={open}
      classNames={{
        root: '!left-[72px] relative',
        content: '!p-0 gap-0',
      }}
      header={
        <span className="text-title-large text-nowrap">
          {t('Cabinet frame structure')}
        </span>
      }
      content={
        <>
          <Form
            form={form}
            labelAlign="left"
            labelWrap
            colon={false}
            layout="vertical"
            onValuesChange={changedValues => {
              debouncedValidate(changedValues);
            }}
            validateTrigger={['onChange']}
            className="left-cabinet-design-menu-body flex flex-col items-start overflow-hidden p-8 gap-8 grow self-stretch"
          >
            <Collapse
              expandIcon={({ isActive }) => (
                <span className="w-16 h-16 flex">
                  {isActive ? <Icon.ICExpandLess /> : <Icon.ICExpandMore />}
                </span>
              )}
              onChange={() => {}}
              className="self-stretch overflow-auto"
              defaultActiveKey={['frame', 'backPlank', 'legs']}
              items={formLayout.map(panel => ({
                ...panel,
                onKeyDown: e => {
                  e.stopPropagation();
                },
              }))}
            />
          </Form>
        </>
      }
    />
  );
};

export default LeftCabinetCustomDesignMenu;
